<template>
  <net-canvas
    ref="canvasElement"
    mode="edit"
    :tool="netTools.tool.id"
    :relations="relations"
    :targets="targets"
    :resources="resources"
    :indices="indices"
    :flows="flows"
    :flow-branches="flowBranches"
    :element-values="elementValues"
    :selection="selection"
    :net-transform="sceneTree.netTransform"
    :impact-source-element="modelViewStore.impactSourceElement ?? undefined"
    :flow-source-element="modelViewStore.flowSourceElement ?? undefined"
    :relation-draft="relationBuilder.relationDraft ?? undefined"
    @select-item="selectItem"
    @add-select-item="addToSelection"
    @select-many="selectMany"
    @clear-all-selection="deselectAll"
    @delete="deleteSelected"
    @update:element-sizes="saveElementSizes"
    @contextmenu="onContextMenu($event)"
    @toggle-relation-mode="toggleRelationMode"
    @toggle-flow-mode="toggleFlowMode"
    @update:net-transform="sceneTree.updateNetTransform"
    @update:canvas-dimensions="updateCanvasDimensions($event)"
    @update:element-value="updateElementValue($event)"
    @create="onCreate"
    @update:tool="netTools.setTool($event)"
    @copy="onCopy"
  >
    <template #default>
      <net-choosing-overlay
        v-if="elementRelationOverlay.element"
        :types="
          elementRelationOverlay.type == 'outgoing'
            ? ['target', 'resource']
            : ['target', 'resource', 'index']
        "
        :targets="targets"
        :resources="resources"
        :indices="indices"
        :selection="elementRelationOverlay.selected"
        :fixed-element="elementRelationOverlay.element"
        :instruction-text="
          elementRelationOverlay.type == 'outgoing'
            ? $t('texts.chooseOutgoingItems')
            : $t('texts.chooseIncomingItems')
        "
        @choose="onResourceRelationsChoose"
      />

      <net-choosing-overlay
        v-if="indexRelationOverlay.index"
        :types="['target', 'resource']"
        :targets="targets"
        :resources="resources"
        :indices="indices"
        :selection="indexRelationOverlay.selected"
        :fixed-element="indexRelationOverlay.index"
        :instruction-text="$t('texts.chooseOutgoingItems')"
        @choose="onIndexRelationsChoose"
      />
    </template>

    <template #canvas>
      <net-toolbar></net-toolbar>
      <net-sidebar-toggle-button></net-sidebar-toggle-button>
    </template>
  </net-canvas>
</template>

<script setup lang="ts">
import { reactive, ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { cloneDeep } from "lodash-es";
import { showError } from "@core/ui/alert/alert";
import { isUidOfType, splitUid, type Uid } from "@/helpers/utility/Id";
import {
  fileToDataURL,
  getDataUrlDimensions,
  openFile,
  warnIfFileIsLarge
} from "@/helpers/utility/File";
import { eventHub } from "@/helpers/utility/Events";
import { notNull } from "@/helpers/utility/Type";
import { switchToEditorArea } from "@/router/router";

import { isSceneElementNode } from "@editor/items/sceneElement/sceneElementFunctions";
import { isSceneShapeNode } from "@editor/items/sceneShape/sceneShapeFunctions";
import { isSceneChartNode } from "@editor/items/sceneChart/sceneChartFunctions";
import { isLayerTreeNode } from "@editor/items/sceneLayer/sceneLayerFunctions";
import { getAllDescendantUids } from "@editor/items/scene/sceneNodeFunctions";

import NetCanvas from "@editor/net/NetCanvas.vue";
import NetToolbar from "./NetToolbar.vue";
import NetSidebarToggleButton from "@editor/net/NetSidebarToggleButton.vue";
import NetChoosingOverlay from "@editor/net/NetChoosingOverlay.vue";

import {
  useSelectionStore,
  type SelectableType
} from "@editor/store/editor/selection.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useQuestionStore } from "@editor/items/question/question.store";
import { useRightClickMenu } from "@core/ui/rightClickMenu.store";
import { useTemplateStore } from "@editor/items/template/templates.store";
import { useNetStore } from "@editor/net/net.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useFeatures } from "@editor/store/features.store";
import { useFlowStore } from "@editor/items/flow/flows.store";
import { useFlowBranchStore } from "@editor/items/flow/flowBranches.store";
import { useModelViewStore } from "./modelView.store";
import { useElements } from "@editor/items/element/elements";
import { useNetTools } from "@editor/net/NetTools";
import { useRelationBuilder } from "@editor/items/relation/relationBuilder";
import { useModelCopyPaste } from "./modelCopyPaste";
import { useEventListener, useIntervalFn } from "@vueuse/core";
import { useUndo } from "@editor/store/undo/undo.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useSceneTree } from "@editor/items/scene/sceneTree";
import { useSceneNodesStore } from "@editor/items/scene/sceneNodes.store";
import { useSceneTextStore } from "@editor/items/sceneText/sceneTexts.store";
import { useSceneMediumStore } from "@editor/items/sceneMedium/sceneMedia.store";
import { useSceneShapeStore } from "@editor/items/sceneShape/sceneShapes.store";
import { useSceneChartStore } from "@editor/items/sceneChart/sceneCharts.store";
import { usePresenceStore } from "@editor/store/presence.store";
import { useSceneElementStore } from "@editor/items/sceneElement/sceneElements.store";
import { useEvaluationStore } from "@editor/items/evaluation/evaluations.store";
import { useChartStore } from "@editor/items/charts/charts.store";
import { useEvaluationViewStore } from "../evaluation/evaluationView.store";
import { useSceneRelationSettingsStore } from "@editor/items/relation/sceneRelationSettings.store";
import { useSceneFlowNodeStore } from "@editor/items/flow/sceneFlowNodes";

import type { ContextmenuAction } from "@core/ui/rightClickMenu.store";
import type { TargetItem } from "@editor/items/target/TargetItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { CanvasSelection, ContextItemType, NetSelectableItemType } from "@editor/net/NetCanvasProps";
import type { ElementItem } from "@editor/items/element/Element";
import type { SceneNodeItem } from "@editor/items/scene/SceneNodeItem";
import { useRandomValuesStore } from "@editor/items/expression/expressionRandomValues.ts";

const selectionStore = useSelectionStore();
const relationStore = useRelationStore();
const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const templateStore = useTemplateStore();
const indexStore = useIndexStore();
const flowStore = useFlowStore();
const flowBranchStore = useFlowBranchStore();
const sceneFlowNodeStore = useSceneFlowNodeStore();
const sceneTextStore = useSceneTextStore();
const sceneElementStore = useSceneElementStore();
const sceneMediumStore = useSceneMediumStore();
const sceneShapeStore = useSceneShapeStore();
const sceneChartStore = useSceneChartStore();
const sceneNodeStore = useSceneNodesStore();
const modelViewStore = useModelViewStore();
const undoStore = useUndo();

const netTools = useNetTools();
const features = useFeatures();

const { t } = useI18n();
const canvasElement = ref<InstanceType<typeof NetCanvas>>();

const { copyItems } = useModelCopyPaste();

// ======================
// ITEMS

const relations = computed(() => relationStore.items);
const targets = computed(() => targetStore.items);
const resources = computed(() => resourceStore.items);
const indices = computed(() => indexStore.items);
const flows = computed(() => flowStore.items);
const flowBranches = computed(() => flowBranchStore.items);
const sceneTree = useSceneTree();
const presenceStore = usePresenceStore();

const question = useQuestionStore();

const elementValues = computed(() => useElements().elementValues);

// ======================
// CONTEXT MENU

function onContextMenu(contextMenuEvent: {
  item?:
    | { uid: Uid<ContextItemType> }
    | null;
  items?: { uid: Uid<"sceneNode" | "relation" | "flow" | "flowBranch"> }[] | null;
  netPosition: { x: number; y: number };
  mousePosition: { x: number; y: number };
  path?: Uid<"sceneNode">[];
}) {
  const { netPosition, mousePosition } = contextMenuEvent;
  let { item, items } = contextMenuEvent;
  const actions: ContextmenuAction[] = [];

  if (items && items.length === 1) {
    item = items[0];
    items = undefined;
  }

  // build a list of actions for the context menu
  if (items && items.length > 0) {
    // multiple items selected
    actions.push({
      title: t("actions.copy"),
      callback: () => {
        const fullItems = items
          .map((i) => {
            const { uid } = i;
            const { type, id } = splitUid(uid);
            if (type === "sceneNode") {
              return sceneNodeStore.items[id];
            } else {
              return null;
            }
          })
          .filter(notNull);
        copyItems(fullItems);
      }
    });

    // If any of the selected items is part of a group, offer to ungroup
    const itemNodes = items
      .map(({ uid }) => isUidOfType(uid, "sceneNode") ? sceneTree.treeNodes.get(uid) : null)
      .filter(notNull);
    if (itemNodes.some((n) => {
      if (!n.parentUid) return false;
      const parentNode = sceneTree.treeNodes.get(n.parentUid);
      if (parentNode && parentNode.node.dataType != "layer") return true;
      return false;
    })) {
      actions.push({
        title: t("actions.ungroup"),
        callback: () => {
          // Ungroup the highest selected group
          const groups = itemNodes
            .map((n) => {
              if (!n.parentUid) return null;
              const parentNode = sceneTree.treeNodes.get(n.parentUid);
              if (!parentNode) return null;
              return parentNode;
            })
            .filter(notNull);

          if (groups.length === 0) return;
          const highestGroup = groups.reduce((prev, current) => {
            const prevNode = sceneTree.treeNodes.get(prev.uid);
            const currentNode = sceneTree.treeNodes.get(current.uid);
            if (!prevNode || !currentNode) return prev;
            if (prevNode.level > currentNode.level) return prev;
            return current;
          });
          if (highestGroup) {
            const groupChildren = sceneNodeStore.byParentSceneNodeUid[highestGroup.uid] || [];
            sceneNodeStore.ungroup(highestGroup);
            selectionStore.selectMany(groupChildren);
          }
        }
      });
    }

    actions.push(
      {
        title: t("actions.group"),
        callback: () => {
          const uids = items
            .map((i) => i.uid)
            .filter(notNull)
            .filter((uid) => isUidOfType(uid, "sceneNode"));
          sceneNodeStore.group(uids);
        }
      },
      {
        title: t("actions.deleteSelectedItems"),
        class: "context-menu__entry--danger",
        callback: () => {
          deleteSelected();
        }
      }
    );
  } else if (item) {
    const { type, id } = splitUid(item.uid);
    switch (type) {
      case "flowBranch": {
        actions.push({
          title: t("actions.deleteFlowBranch"),
          class: "context-menu__entry--danger",
          callback: () => {
            flowBranchStore.delete({ id });
          }
        });
        break;
      }

      case "flow": {
        actions.push({
          title: t("actions.hide"),
          callback: () => {
            sceneFlowNodeStore.updateOrCreate({
              flowId: id,
              sceneId: sceneTree.activeScene.id,
              visible: false
             });
          }
        }, {
          title: t("actions.deleteFlow"),
          class: "context-menu__entry--danger",
          callback: () => {
            flowStore.delete({ id });
          }
        });
        break;
      }

      case "elementPlaceholder": {
        actions.push({
          title: t("labels.newTarget"),
          callback: () => {
            const template = useTemplateStore().template;
            
            const templatePositions = template?.targetPositions || {};
            const position = templatePositions[id];
            if (!position) {
              return;
            }
            createTarget(position);
          }
        });
        break;
      }

      case "relation": {
        actions.push({
          title: t("actions.hide"),
          callback: () => {
            useSceneRelationSettingsStore().updateOrCreate({
              relationId: id,
              sceneId: sceneTree.activeScene.id,
              visible: false
             });
          }
        }, {
          title: t("labels.deleteRelation"),
          class: "context-menu__entry--danger",
          callback: () => {
            relationStore.delete({ id });
          }
        });
        break;
      }

      case "question": {
        const questionStore = useQuestionStore();
        actions.push({
          title: t("labels.hide"),
          callback: () => {
            questionStore.hide();
          }
        });
        break;
      }

      case "sceneNode": {
        const treeNode = sceneTree.treeNodes.get(`sceneNode_${id}`);
        if (!treeNode) break;
        const sceneNode = treeNode.node;
        if (isSceneElementNode(sceneNode)) {
          const elementUid = sceneNode.data.elementUid;
          const { type, id } = splitUid(elementUid);

          if (type == "target" || type == "resource") {
            if (features.impact) {
              const impactSourceElement = modelViewStore.impactSourceElement;
              if (
                impactSourceElement && elementUid == impactSourceElement.uid
              ) {
                actions.push({
                  title: t("actions.endRelationMode"),
                  callback: () => {
                    toggleRelationMode({ uid: elementUid } );
                  }
                });
              } else {
                actions.push({
                  title: t("actions.startRelationMode"),
                  callback: () => {
                    toggleRelationMode({ uid: elementUid } );
                  }
                });
              }
            }
  
            actions.push(
              {
                title: t("actions.createOutgoingRelation"),
                callback: () => {
                  elementRelationOverlay.type = "outgoing";
                  elementRelationOverlay.element = { uid: `${type}_${id}` };

                  const relations = relationStore.byStartUid[`${type}_${id}`] ?? [];

                  const selected = relations.map((rel) => {
                    if (rel.endItemType === "target") {
                      return targets.value[rel.endItemId];
                    } else {
                      return resources.value[rel.endItemId];
                    }
                  });

                  elementRelationOverlay.selected = selected;
                }
              },
              {
                title: t("actions.createIncomingRelation"),
                callback: () => {
                  elementRelationOverlay.type = "incoming";
                  elementRelationOverlay.element = { uid: `${type}_${id}` };
                  const relations = relationStore.byEndUid[`${type}_${id}`] ?? [];

                  const selected = relations.map((rel) => {
                    if (rel.startItemType === "target") {
                      return targets.value[rel.startItemId];
                    } else {
                      return resources.value[rel.startItemId];
                    }
                  });

                  elementRelationOverlay.selected = selected;
                }
              }
            );
          } else if (type == "index") {
            actions.push({
              title: t("actions.createOutgoingRelation"),
              callback: () => {
                const indexUid = `index_${id}` as const;
                indexRelationOverlay.type = "outgoing";
                indexRelationOverlay.index = { uid: indexUid };

                const relations = relationStore.byStartUid[indexUid] ?? [];

                const selected = relations.map((rel) => {
                  if (rel.endItemType === "target") {
                    return targets.value[rel.endItemId];
                  } else {
                    return resources.value[rel.endItemId];
                  }
                });

                indexRelationOverlay.selected = selected;
              }
            });
          }
        }

        if (sceneNode.parentSceneNodeUid != null) {
          const parentNode = sceneNodeStore.byUid[sceneNode.parentSceneNodeUid];
          if (parentNode && parentNode.dataType != "layer") {
            actions.push({
              title: t("actions.ungroup"),
              callback: () => {
                sceneNodeStore.ungroup(sceneNode);
              }
            });
          }
        }

        if (isSceneShapeNode(sceneNode)) {
          // Specific actions for shape nodes
          actions.push({
            title: t("actions.editShape"),
            callback: () => {
              selectItem(sceneNode);
              eventHub.emit("scene-shape-edit-requested", sceneNode.uid);
            }
          });
        }

        if (isSceneChartNode(sceneNode)) {
          // Specific actions for chart nodes
          actions.push({
            title: t("actions.copyToEvaluation"),
            callback: async () => {
              const chart = useChartStore().byUid[sceneNode.data.chartUid];
              const evaluation = await useEvaluationStore().create({
                chartType: chart.chartType,
                data: cloneDeep(chart.data),
                chartSettings: cloneDeep(chart.chartSettings),
                itemConfigs: cloneDeep(chart.itemConfigs),
                dataToggles: cloneDeep(chart.dataToggles)
              });
              useEvaluationViewStore().openTab(evaluation);
              switchToEditorArea("evaluation");
            }
          });
        }

        actions.push(
          {
            title: t("actions.moveToFront"),
            callback: () => {
              sceneNodeStore.moveToFront(sceneNode);
            }
          },
          {
            title: t("actions.moveToBack"),
            callback: () => {
              sceneNodeStore.moveToBack(sceneNode);
            }
          }
        );

        if (isSceneElementNode(sceneNode)) {
          actions.push({
            title: t("actions.removeElementInstance"),
            callback: () => {
              sceneNodeStore.delete(sceneNode, { deleteElement: false });
            }
          });
        }
        
        actions.push({
          title: t("labels.delete"),
          class: "context-menu__entry--danger",
          callback: () => {
            sceneNodeStore.delete(sceneNode);
          }
        });
        break;
      }

      default: {
        console.log("CONTEXT MENU OF", JSON.parse(JSON.stringify(item)));
      }
    }
  } else {
    if (features.target) {
      actions.push({
        title: t("labels.newTarget"),
        callback: () => {
          createTarget(netPosition);
        }
      });
    }

    if (features.resource) {
      actions.push({
        title: t("labels.newResource"),
        callback: () => {
          createResource(netPosition);
        }
      });
    }

    if (features.index) {
      actions.push({
        title: t("labels.newIndex"),
        callback: async () => {
          createIndex(netPosition);
        }
      });
    }

    if (features.design) {
      actions.push({
        title: t("actions.createText"),
        callback: async () => {
          createText(netPosition);
        }
      });
      actions.push({
        title: t("actions.createImage"),
        callback: async () => {
          createMedium(netPosition);
        }
      });

      actions.push({
        title: t("actions.createShape"),
        callback: async () => {
          createShape(netPosition);
        }
      });
    }

    if (features.netChart) {
      actions.push({
        title: t("actions.createChart"),
        callback: async () => {
          createChart(netPosition);
        }
      });
    }

    if (!question.hidden) {
      actions.push({
        title: t("actions.hideQuestion"),
        callback: () => {
          question.hide();
        }
      });
    } else {
      actions.push({
        title: t("actions.showQuestion"),
        callback: () => {
          question.show();
        }
      });
    }

    actions.push({
      title: t("actions.adjustView"),
      callback: () => {
        const net = canvasElement.value;
        if (net) {
          net.adjustView();
        }
      }
    });
  }

  const rightClickMenu = useRightClickMenu();

  rightClickMenu.show({
    actions,
    x: mousePosition.x,
    y: mousePosition.y
  });
}

// ======================
// RELATION OVERLAYS

const elementRelationOverlay = reactive({
  type: "outgoing" as "outgoing" | "incoming",
  element: null as { uid: Uid<"target" | "resource">} | null,
  selected: [] as (TargetItem | ResourceItem)[]
});

const indexRelationOverlay = reactive({
  type: "outgoing" as "outgoing" | "incoming",
  index: null as { uid: Uid<"index"> } | null,
  selected: [] as (TargetItem | ResourceItem)[]
});

/**
 * Move into relation builder mode (create relations to other elements)
 */
function toggleRelationMode(item: { uid: Uid<"target" | "resource" | "index">}) {
  netTools.setTool("pointer");

  if (modelViewStore.impactSourceElement?.uid === item?.uid) {
    modelViewStore.impactSourceElement = null;
    selectionStore.select(item);
  } else {
    modelViewStore.flowSourceElement = null;
    modelViewStore.impactSourceElement = {
      uid: item.uid
    };
  }
}

function toggleFlowMode(item: { uid: Uid<"target" | "resource">}) {
  netTools.setTool("pointer");

  if (modelViewStore.flowSourceElement?.uid === item?.uid) {
    modelViewStore.flowSourceElement = null;
    selectionStore.select(item);
  } else {
    modelViewStore.impactSourceElement = null;
    modelViewStore.flowSourceElement = {
      uid: item.uid
    };
  }
}

function onResourceRelationsChoose(
  items: (TargetItem | ResourceItem | IndexItem)[]
) {
  const element = elementRelationOverlay.element;
  if (element == null) return;
  elementRelationOverlay.element = null;

  if (elementRelationOverlay.type == "outgoing") {
    const { type, id } = splitUid(element.uid);
    relationStore.updateOutgoingConnections(items, {
      startItemType: type,
      startItemId: id
    });
  } else {
    const { type, id } = splitUid(element.uid);
    relationStore.updateIncomingConnections(items, {
      endItemType: type,
      endItemId: id
    });
  }
}

function onIndexRelationsChoose(items: (TargetItem | ResourceItem | IndexItem)[]) {
  const index = indexRelationOverlay.index;
  if (index == null) return;
  indexRelationOverlay.index = null;

  const { id } = splitUid(index.uid);
  relationStore.updateOutgoingConnections(items, {
    startItemType: "index",
    startItemId: id,
    updateType: "change"
  });
}

// ======================
// CREATE ITEMS

async function onCreate(box: {
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  const tool = netTools.tool;

  if (tool.type != "creating") return;

  if (tool.id == "target") {
    createTarget({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height
    });
  } else if (tool.id == "resource") {
    createResource({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height
    });
  } else if (tool.id == "index") {
    createIndex({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height
    });
  } else if (tool.id == "text") {
    createText({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height
    });
  } else if (tool.id == "medium") {
    createMedium({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height
    });
  } else if (tool.id == "shape") {
    createShape({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height
    });
  } else if (tool.id == "chart") {
    createChart({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height
    });
  }

  // reset the tool to the pointer
  // after creating an element
  netTools.setTool("pointer");
}

async function createTarget(position: {
  x: number;
  y: number;
  width?: number;
  height?: number;
}) {
  // don't create a target if the limit is reached
  if (targetStore.length >= templateStore.elementLimit) {
    showError(t("errors.maxElementCountReached"));
    return;
  }

  const { x, y, width, height } = position;

  const target = await sceneElementStore.create(
    {
      x,
      y,
      width: Math.max(200, width ?? 200),
      height: Math.max(100, height ?? 100)
    },
    {
      type: "target",
      title: ""
    }
  );

  selectItem(target);

  return target;
}

async function createResource(position: {
  x: number;
  y: number;
  width?: number;
  height?: number;
}) {
  const { x, y, width, height } = position;

  const resource = await sceneElementStore.create(
    {
      x,
      y,
      width: Math.max(200, width ?? 200),
      height: Math.max(100, height ?? 100)
    },
    {
      type: "resource",
      title: ""
    },
  );

  selectItem(resource);

  return resource;
}

async function createIndex(position: {
  x: number;
  y: number;
  width?: number;
  height?: number;
}) {
  const { x, y, width, height } = position;

  const indexNode = await sceneElementStore.create(
    {
      x,
      y,
      width: Math.max(200, width ?? 200),
      height: Math.max(100, height ?? 100)
    }, {
      type: "index",
      title: ""
    }
  );

  selectItem(indexNode);

  return indexNode;
}

async function createText(position: {
  x: number;
  y: number;
  width?: number;
  height?: number;
}) {
  const { x, y, width, height } = position;

  const textNode = await sceneTextStore.create({
    x,
    y,
    width: Math.max(40, width || 200),
    height: Math.max(10, height || 18),
  }, {
    text: ""
  });
  selectItem(textNode);

  return textNode;
}

async function createMedium(position: {
  x: number;
  y: number;
  width?: number;
  height?: number;
}) {
  const { x, y, width: passedWidth, height: passedHeight } = position;

  // let the user choose the medium from the file system first.
  // for this, open the file dialog and let the user choose a file.
  // then, create the medium with the chosen file.

  let file: File | null = null;
  let originalWidth, originalHeight;

  let dataUrl: string;

  try {
    file = await openFile(["image/*", "video/*"]);

    warnIfFileIsLarge(file, 4);

    dataUrl = await fileToDataURL(file);
    const fileDimensions = await getDataUrlDimensions(dataUrl, file.type);

    originalWidth = fileDimensions.width;
    originalHeight = fileDimensions.height;
  } catch (error) {
    console.error(error);
    showError(t("errors.mediaFileOpenError"));
    return;
  }

  const baseWidth = passedWidth || originalWidth;
  const baseHeight = passedHeight || originalHeight;

  let scale = 1;
  if (baseHeight < 20 || baseWidth < 20) {
    scale = 20 / Math.min(baseWidth, baseHeight);
  }

  const width = baseWidth * scale;
  const height = (width / originalWidth) * originalHeight;

  const mediumNode = await sceneMediumStore.create(
    {
      x,
      y,
      width,
      height
    },
    {
      data: dataUrl,
      mediaType: file.type
    },
  );

  selectItem(mediumNode);

  return mediumNode;
}

async function createShape(position: {
  x: number;
  y: number;
  width?: number;
  height?: number;
}) {
  let { x, y, width, height } = position;

  if (width == 0) {
    width = sceneShapeStore.sizeDefaults.width;
  }
  if (height == 0) {
    height = sceneShapeStore.sizeDefaults.height;
  }

  const shapeNode = await sceneShapeStore.create({
    x,
    y,
    width: Math.max(10, width ?? 200),
    height: Math.max(10, height ?? 100),
  }, {});

  selectItem(shapeNode);

  return shapeNode;
}

async function createChart(position: {
  x: number;
  y: number;
  width?: number;
  height?: number;
}) {
  let { x, y, width, height } = position;

  if (width == 0) {
    width = 300;
  }
  if (height == 0) {
    height = 200;
  }

  const chartNode = await sceneChartStore.create({
    data: {
      simulation: [-1],
      simulationRound: [0, useSettingsStore().simulationRoundAmount - 1]
    }
  }, {
    x,
    y,
    width: Math.max(100, width ?? 300),
    height: Math.max(100, height ?? 200)
  });

  selectItem(chartNode);

  return chartNode;
}

// ======================
// LISTENERS

useEventListener(window, "keydown", onKeyDown);

function onKeyDown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    if (modelViewStore.impactSourceElement) {
      modelViewStore.impactSourceElement = null;
      event.preventDefault();
      event.stopPropagation();
    }
    if (modelViewStore.flowSourceElement) {
      modelViewStore.flowSourceElement = null;
      event.preventDefault();
      event.stopPropagation();
    }
  }

  if (event.key === "g" && (event.ctrlKey || event.metaKey)) {
    console.log("ctrl + g", event);
    // ctrl+G or cmd+G
    const selectedUids = Array.from(selectionStore.selectedUids);
    const nodes = selectedUids
      .filter((uid) => isUidOfType(uid, "sceneNode"))
      .map((uid) => sceneTree.treeNodes.get(uid))
      .filter(notNull);
    sceneNodeStore
      .group(nodes.map((node) => node.uid))
      .then((group) => {
        if (!group) return;
        selectionStore.selectMany([group, ...nodes]);
      });
    event.preventDefault();
    event.stopPropagation();
  }
}

// ======================
// COPY

/**
 * This function is called when items are copied via alt-dragging
 * in the net canvas.
 */
async function onCopy(
  items: {
    x: number;
    y: number;
    item: {
      uid: Uid<"sceneNode">;
    };
  }[]
) {
  const undoAction = undoStore.startGroup("model", "duplicateItems");

  const copiedItems = [];

  for (const itemCopy of items) {
    const { x, y, item } = itemCopy;

    if (isUidOfType(item.uid, "sceneNode")) {
      const { id } = splitUid(item.uid);
      // we're duplicating one item after the other
      // to make sure that any name uniqueness constraints are properly handled by the store
      const duplicatedNode = await sceneNodeStore.duplicate(id, { x, y });
      if (duplicatedNode) {
        copiedItems.push(duplicatedNode);
      }
    }
  }

  selectMany(copiedItems);

  undoStore.endGroup("model", undoAction);
}

// ======================
// ELEMENT VALUES

function updateElementValue({
  uid,
  value
}: {
  uid: ElementItem["uid"];
  value: number;
}) {
  const { type, id } = splitUid(uid);
  if (type === "target") {
    targetStore.update({
      id: id,
      value
    });
  } else if (type === "resource") {
    resourceStore.update({
      id: id,
      value
    });
  }
}

// ======================
// RANDOM VALUES

const randomValuesStore = useRandomValuesStore();

useIntervalFn(() => {
  randomValuesStore.reset();
}, 5000, { immediate: true });

// ======================
// SELECTION

const selection = computed((): CanvasSelection => {
  const selection = { ...selectionStore.selection };

  if (modelViewStore.impactSourceElement) {
    const uid = modelViewStore.impactSourceElement.uid;
    selection.active = new Set([uid]);
  }

  return selection;
});

function selectItem(
  payload: { uid: Uid<NetSelectableItemType> } | null,
  path?: Uid<"sceneNode">[]
) {
  if (payload == null) {
    selectionStore.clearAll();
    return;
  }

  const { uid } = payload;

  const treeNode = isUidOfType(uid, "sceneNode") ? sceneTree.treeNodes.get(uid) : null;

  if (treeNode) {
    const sceneNode = treeNode.node;
    if (isSceneElementNode(sceneNode)) {
      const { type } = splitUid(sceneNode.data.elementUid);
      if (modelViewStore.impactSourceElement != null) {
        if (type === "index") return;
        if (type !== "target" && type !== "resource") {
          modelViewStore.impactSourceElement = null;
        }
      }
      if (modelViewStore.flowSourceElement != null) {
        if (type === "index") return;
        if (type !== "target" && type !== "resource") {
          modelViewStore.flowSourceElement = null;
        }
      }
    }
  }
  path = path || (treeNode ? treeNode.path : []);
  const pathNodes = path
    .map((uid) => sceneTree.treeNodes.get(uid))
    .filter(notNull);

  selectionStore.deselectStakeholders();

  const layerNode = pathNodes.find((treeNode) => isLayerTreeNode(treeNode));
  if (layerNode && layerNode.node.data.layerType === "scene") {
    presenceStore.setActiveLayer(layerNode.uid);
  }

  const selectedParts = path.filter((uid) => {
    return selectionStore.selectedUids.has(uid as Uid<SelectableType>);
  });

  if (
    !modelViewStore.impactSourceElement &&
    !modelViewStore.flowSourceElement
  ) {
    // select groups if not selected:
    for (const treeNode of pathNodes) {
      const sceneNode = treeNode.node;
      if (sceneNode.dataType === "group") {
        if (selectedParts.length == 0) {
          const descendantUids = getAllDescendantUids(sceneNode);
          // Any of the group's children is selected?
          if (
            [...descendantUids].some((nodeUid) =>
              selectionStore.selectedUids.has(nodeUid as Uid<SelectableType>)
            )
          ) {
            // skip groups that have selected children
            continue;
          }
          console.log("selecting group", sceneNode.uid);
          selectWithChildren(sceneNode);
          return;
        }

        if (selectedParts.includes(sceneNode.uid)) {
          // group is already selected
          removeGroupFromSelection(sceneNode, path);
          return;
        }
      }
    }
  }

  if (uid == null) return;

  const { type, id } = splitUid(uid);

  return selectionStore.select({ type, id });
}

function selectWithChildren(node: SceneNodeItem) {
  undoStore.startGroup("model", "selectWithChildren");

  const descendantUids = getAllDescendantUids(node);
  const itemsToSelect = Array.from(descendantUids).map((uid) => {
    const { type, id } = splitUid(uid);
    return { type, id };
  });
  itemsToSelect.push({ type: "sceneNode", id: node.id });
  selectionStore.selectMany(itemsToSelect);

  undoStore.endGroup("model");
}

/**
 * Recursively remove a group and its children from the selection,
 * @param group The group to remove from selection
 * @param path The selection path, including all items that should be kept selected
 */
function removeGroupFromSelection(
  group: SceneNodeItem,
  path: Uid<string>[] = []
) {
  selectionStore.remove(group);

  console.log("remove group from selection", group.uid);

  const childrenNodes = sceneNodeStore.byParentSceneNodeUid[group.uid] || [];
  for (const childNode of childrenNodes) {
    const childUid = childNode.uid;
    if (path.includes(childUid)) continue;
    const { type, id } = splitUid(childUid);
    selectionStore.remove({ type, id });
    if (childNode.dataType === "group") {
      removeGroupFromSelection(childNode, path);
    }
  }
}

function selectMany(
  items: { uid: Uid<NetSelectableItemType> }[]
) {
  if (items.length === 1) {
    return selectItem(items[0]);
  }
  return selectionStore.selectMany(
    items.map((item) => {
      const { type, id } = splitUid(item.uid);
      return { type, id };
    })
  );
}

function addToSelection(
  item: { uid: Uid<NetSelectableItemType> } | null,
  path: Uid<"sceneNode">[] = []
) {
  if (!item) return;
  const { type, id } = splitUid(item.uid);

  if (
    !modelViewStore.impactSourceElement &&
    !modelViewStore.flowSourceElement
  ) {
    const selectedParts = path.filter((uid) => {
      return selectionStore.selectedUids.has(uid as Uid<NetSelectableItemType>);
    });

    // select groups if not selected:
    for (const uid of path) {
      const sceneNode = sceneNodeStore.byUid[uid];
      if (!sceneNode) continue;

      if (sceneNode.dataType == "layer") continue;

      if (selectedParts.includes(uid)) {
        removeGroupFromSelection(sceneNode, path);
        return;
      }

      // TODO: Check this behaviour.

      const nestedChildUids = getAllDescendantUids(sceneNode);

      // Any of the group's children is selected?
      if (
        [...nestedChildUids].some((childUid) =>
          selectionStore.selectedUids.has(childUid as Uid<NetSelectableItemType>)
        )
      ) {
        // skip groups that have selected children
        continue;
      }

      if (selectedParts.length == 0) {
        selectionStore.add(sceneNode);
        return;
      }
    }
  }

  return selectionStore.add({ type, id }, true);
}

function deselectAll() {
  modelViewStore.impactSourceElement = null;
  modelViewStore.flowSourceElement = null;
  selectionStore.clearAll();
}

async function deleteSelected() {
  undoStore.startGroup("model", "deleteSelectedItems");
  await selectionStore.deleteSelected();
  undoStore.endGroup("model");
}

async function saveElementSizes(
  newItemSizes: {
    uid: Uid<"sceneNode" | "flow">;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
  }[]
) {
  undoStore.startGroup("model", "updateElementSizes");

  for (const newItemSize of newItemSizes) {
    const { id, type } = splitUid(newItemSize.uid);
    
    const size = {
      id: id,
      x: newItemSize.x,
      y: newItemSize.y,
      width: newItemSize.width,
      height: newItemSize.height,
      rotation: newItemSize.rotation
    };
    if (type === "sceneNode") {
      sceneNodeStore.update(size);
    } else if (type === "flow") {
      await sceneFlowNodeStore.updateOrCreate({
        flowId: id,
        sceneId: sceneTree.activeScene.id,
        x: newItemSize.x,
        y: newItemSize.y,
      });
    } else {
      console.warn("Unknown item type for size update:", type);
      continue;
    }
  }

  undoStore.endGroup("model");
}

// ======================
// KEY EVENTS

function onWindowKeyDown(keyEvent: KeyboardEvent) {
  if (keyEvent.defaultPrevented) return;

  const target = keyEvent.target;
  if (!(target instanceof HTMLElement)) return;

  const isEditable = target.isContentEditable;
  if (isEditable) return;

  if (target instanceof HTMLInputElement) return;

  netTools.selectByHotkey(keyEvent.key);
}
onMounted(() => {
  window.addEventListener("keydown", onWindowKeyDown);
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onWindowKeyDown);
});

// ======================
// NET TRANSFORM

function updateCanvasDimensions(dimensions: {
  scale: number;
  x: number;
  y: number;
  width: number;
  height: number;
  left: number;
  top: number;
  right: number;
  bottom: number;

  translateX: number;
  translateY: number;

  containerX: number;
  containerY: number;
  containerWidth: number;
  containerHeight: number;
}) {
  useNetStore().canvasDimensions = dimensions;
}

// ======================
// RELATION DRAFT

const relationBuilder = useRelationBuilder();
</script>

<style></style>
